import { useEffect, useMemo, useRef } from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useEventContext } from '@providers/EventProvider';
import { useClientSpecificTeleportVisibility } from '@hooks/clientSpecificUtils';

import { useTeleportOpen } from './useTeleportOpen';

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
    return true;
  }

  if (target.isContentEditable) {
    return true;
  }

  return Boolean(target.closest('[contenteditable="true"]'));
};

export const useTeleportKeyboardShortcut = () => {
  const { eventLoaded, teleportEnabled, currentEvent } = useMytaverseEvent();
  const { isEventRun } = useEventContext();
  const { isTeleportFeatureEnabled } = useClientSpecificTeleportVisibility();
  const { toggleTeleport, closeTeleport } = useTeleportOpen();

  const toggleTeleportRef = useRef(toggleTeleport);
  toggleTeleportRef.current = toggleTeleport;

  useEffect(() => {
    if (!isTeleportFeatureEnabled) {
      closeTeleport();
    }
  }, [isTeleportFeatureEnabled, closeTeleport]);

  const isTeleportKeyDisabled = useMemo(() => {
    return currentEvent?.disabledKeyCommands?.some(
      (key) => key.toLowerCase() === 't',
    );
  }, [currentEvent?.disabledKeyCommands]);

  useEffect(() => {
    const isShortcutEnabled =
      eventLoaded &&
      isEventRun &&
      teleportEnabled &&
      isTeleportFeatureEnabled &&
      !isTeleportKeyDisabled;

    if (!isShortcutEnabled) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code !== 'KeyT' && event.key.toLowerCase() !== 't') {
        return;
      }

      if (event.repeat) {
        return;
      }

      if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      if (isTypingTarget(event.target)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      toggleTeleportRef.current();
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [
    eventLoaded,
    isEventRun,
    teleportEnabled,
    isTeleportFeatureEnabled,
    isTeleportKeyDisabled,
  ]);
};
